/**
 * TAMIX SAAS PLATFORM - Dual Authentication Component
 * ==================================================
 * Supports Email/Password Login, Registration, QA Quick-Fill Presets, and Google OIDC
 */

import React, { useEffect, useState } from 'react'
import { useAuthStore } from '@/store/authStore'
import { authApi } from '@/services/authApi'

type AuthMode = 'login' | 'register'

const LoginForm: React.FC = () => {
  const { isLoading, error, loginWithGoogle, loginWithCredentials, registerUser, login, setError, clearError } = useAuthStore()
  
  const [mode, setMode] = useState<AuthMode>('login')
  const [showPassword, setShowPassword] = useState(false)
  const [isProcessingCallback, setIsProcessingCallback] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')

  // Login form state
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  // Register form state
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [regEmail, setRegEmail] = useState('')
  const [regPassword, setRegPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [jobTitle, setJobTitle] = useState('')

  // Handle Google OAuth callback on component mount
  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        const authResponse = authApi.parseGoogleAuthResponse()
        
        if (authResponse) {
          setIsProcessingCallback(true)
          
          if (authResponse.error) {
            setError(
              authResponse.error_description || 
              `Error de autenticación: ${authResponse.error}`
            )
            window.history.replaceState({}, document.title, window.location.pathname)
            return
          }

          if (authResponse.code) {
            const loginResponse = await authApi.handleGoogleCallback(authResponse.code)
            login(loginResponse.access_token, loginResponse.user)
            window.history.replaceState({}, document.title, window.location.pathname)
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error de autenticación')
        window.history.replaceState({}, document.title, window.location.pathname)
      } finally {
        setIsProcessingCallback(false)
      }
    }

    handleAuthCallback()
  }, [login, setError])

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    clearError()
    setSuccessMessage('')

    if (!email.trim() || !password) {
      setError('Por favor ingresa tu correo y contraseña.')
      return
    }

    try {
      await loginWithCredentials(email.trim(), password)
    } catch {
      // Error handled by store
    }
  }

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    clearError()
    setSuccessMessage('')

    if (!firstName.trim() || !lastName.trim() || !regEmail.trim() || !regPassword) {
      setError('Por favor completa todos los campos requeridos.')
      return
    }

    if (regPassword.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres.')
      return
    }

    if (regPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden.')
      return
    }

    try {
      await registerUser({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: regEmail.trim(),
        password: regPassword,
        job_title: jobTitle.trim() || undefined,
      })
      setSuccessMessage('¡Cuenta creada e inicio de sesión exitoso!')
    } catch {
      // Error handled by store
    }
  }

  // Quick-fill presets for QA and client testing
  const handleQuickFill = (presetEmail: string, presetPass: string) => {
    clearError()
    setMode('login')
    setEmail(presetEmail)
    setPassword(presetPass)
  }

  const isProcessing = isLoading || isProcessingCallback

  return (
    <div className="auth-container d-flex align-items-center justify-content-center min-vh-100 py-5" style={{ background: '#f8fafc' }}>
      <div className="card auth-card shadow-sm border-0" style={{ maxWidth: '460px', width: '100%', borderRadius: '12px' }}>
        <div className="card-body p-4 p-md-5">
          {/* Logo and Title */}
          <div className="text-center mb-4">
            <div className="d-inline-flex align-items-center justify-content-center bg-primary text-white rounded-circle mb-2" style={{ width: '48px', height: '48px' }}>
              <i className="fas fa-shield-alt fa-lg"></i>
            </div>
            <h1 className="h3 fw-bold text-dark mb-1">TAMIX</h1>
            <p className="text-muted small mb-0">
              Plataforma de Evaluación Psicométrica y NOM-035
            </p>
          </div>

          {/* Mode Switch Tabs */}
          <div className="d-flex rounded p-1 mb-4" style={{ background: '#e2e8f0' }}>
            <button
              type="button"
              className={`btn btn-sm flex-fill fw-semibold ${mode === 'login' ? 'btn-white bg-white shadow-sm text-primary' : 'text-secondary'}`}
              onClick={() => { setMode('login'); clearError(); }}
            >
              <i className="fas fa-sign-in-alt me-1"></i> Iniciar Sesión
            </button>
            <button
              type="button"
              className={`btn btn-sm flex-fill fw-semibold ${mode === 'register' ? 'btn-white bg-white shadow-sm text-primary' : 'text-secondary'}`}
              onClick={() => { setMode('register'); clearError(); }}
            >
              <i className="fas fa-user-plus me-1"></i> Crear Cuenta
            </button>
          </div>

          {/* Quick-Fill QA Bar (for testing convenience) */}
          {mode === 'login' && (
            <div className="mb-4 p-2 bg-light rounded border">
              <small className="text-muted d-block mb-1 fw-bold text-uppercase" style={{ fontSize: '0.7rem' }}>
                <i className="fas fa-flask me-1 text-primary"></i> Acceso Rápido QA:
              </small>
              <div className="btn-group btn-group-sm w-100" role="group">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  style={{ fontSize: '0.75rem' }}
                  onClick={() => handleQuickFill('staff@tamix.l3m.mx', 'TamixStaff2026!')}
                  title="Especialista RRHH"
                >
                  Staff
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  style={{ fontSize: '0.75rem' }}
                  onClick={() => handleQuickFill('alumno@tamix.l3m.mx', 'TamixAlumno2026!')}
                  title="Usuario Evaluado"
                >
                  Alumno
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  style={{ fontSize: '0.75rem' }}
                  onClick={() => handleQuickFill('admin@l3m.mx', 'TamixAdmin2026!')}
                  title="Administrador"
                >
                  Admin
                </button>
              </div>
            </div>
          )}

          {/* Alerts */}
          {error && (
            <div className="alert alert-danger alert-dismissible fade show py-2 small" role="alert">
              <i className="fas fa-exclamation-triangle me-2"></i>
              {error}
              <button
                type="button"
                className="btn-close btn-close-sm"
                onClick={clearError}
                aria-label="Cerrar"
              ></button>
            </div>
          )}

          {successMessage && (
            <div className="alert alert-success py-2 small" role="alert">
              <i className="fas fa-check-circle me-2"></i>
              {successMessage}
            </div>
          )}

          {/* Processing Spinner */}
          {isProcessingCallback && (
            <div className="alert alert-info py-2 small" role="alert">
              <div className="d-flex align-items-center">
                <div className="spinner-border spinner-border-sm me-2" role="status">
                  <span className="visually-hidden">Procesando...</span>
                </div>
                Procesando autenticación...
              </div>
            </div>
          )}

          {/* LOGIN FORM */}
          {mode === 'login' ? (
            <form onSubmit={handleLoginSubmit} className="needs-validation">
              <div className="mb-3">
                <label className="form-label small fw-semibold text-secondary">
                  Correo Electrónico
                </label>
                <div className="input-group">
                  <span className="input-group-text bg-light border-end-0 text-muted">
                    <i className="fas fa-envelope"></i>
                  </span>
                  <input
                    type="email"
                    name="email"
                    className="form-control border-start-0 ps-0"
                    placeholder="ejemplo@empresa.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={isProcessing}
                    autoComplete="username"
                  />
                </div>
              </div>

              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center">
                  <label className="form-label small fw-semibold text-secondary mb-0">
                    Contraseña
                  </label>
                </div>
                <div className="input-group mt-1">
                  <span className="input-group-text bg-light border-end-0 text-muted">
                    <i className="fas fa-lock"></i>
                  </span>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    className="form-control border-start-0 border-end-0 ps-0"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isProcessing}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="input-group-text bg-light border-start-0 text-muted"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </button>
                </div>
              </div>

              <div className="d-grid gap-2 mt-4">
                <button
                  type="submit"
                  className="btn btn-primary btn-lg fw-semibold"
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Iniciando sesión...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-sign-in-alt me-2"></i>
                      Iniciar Sesión
                    </>
                  )}
                </button>
              </div>
            </form>
          ) : (
            /* REGISTER FORM */
            <form onSubmit={handleRegisterSubmit} className="needs-validation">
              <div className="row g-2 mb-2">
                <div className="col-6">
                  <label className="form-label small fw-semibold text-secondary mb-1">Nombre</label>
                  <input
                    type="text"
                    name="firstName"
                    className="form-control"
                    placeholder="Nombre"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    disabled={isProcessing}
                  />
                </div>
                <div className="col-6">
                  <label className="form-label small fw-semibold text-secondary mb-1">Apellidos</label>
                  <input
                    type="text"
                    name="lastName"
                    className="form-control"
                    placeholder="Apellidos"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                    disabled={isProcessing}
                  />
                </div>
              </div>

              <div className="mb-2">
                <label className="form-label small fw-semibold text-secondary mb-1">Correo Electrónico</label>
                <input
                  type="email"
                  name="regEmail"
                  className="form-control"
                  placeholder="usuario@empresa.com"
                  value={regEmail}
                  onChange={(e) => setRegEmail(e.target.value)}
                  required
                  disabled={isProcessing}
                  autoComplete="email"
                />
              </div>

              <div className="mb-2">
                <label className="form-label small fw-semibold text-secondary mb-1">Puesto / Cargo (Opcional)</label>
                <input
                  type="text"
                  name="jobTitle"
                  className="form-control"
                  placeholder="Ej. Coordinador de RRHH"
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                  disabled={isProcessing}
                />
              </div>

              <div className="row g-2 mb-3">
                <div className="col-6">
                  <label className="form-label small fw-semibold text-secondary mb-1">Contraseña</label>
                  <input
                    type="password"
                    name="regPassword"
                    className="form-control"
                    placeholder="Mín. 8 caract."
                    value={regPassword}
                    onChange={(e) => setRegPassword(e.target.value)}
                    required
                    minLength={8}
                    disabled={isProcessing}
                    autoComplete="new-password"
                  />
                </div>
                <div className="col-6">
                  <label className="form-label small fw-semibold text-secondary mb-1">Confirmar</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    className="form-control"
                    placeholder="Repetir"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={8}
                    disabled={isProcessing}
                    autoComplete="new-password"
                  />
                </div>
              </div>

              <div className="d-grid gap-2 mt-4">
                <button
                  type="submit"
                  className="btn btn-success btn-lg fw-semibold"
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Registrando...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-user-check me-2"></i>
                      Crear Cuenta
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* Social Divider */}
          <div className="position-relative my-4">
            <hr className="text-muted" />
            <span
              className="position-absolute top-50 start-50 translate-middle bg-white px-3 text-muted small"
              style={{ fontSize: '0.8rem' }}
            >
              o bien
            </span>
          </div>

          {/* Google OAuth Option */}
          <div className="d-grid">
            <button
              type="button"
              className="btn btn-outline-secondary d-flex align-items-center justify-content-center py-2"
              onClick={loginWithGoogle}
              disabled={isProcessing}
            >
              <i className="fab fa-google text-danger me-2"></i>
              Continuar con Google
            </button>
          </div>

          {/* Compliance Badge */}
          <div className="border-top pt-3 mt-4 text-center">
            <small className="text-muted d-block" style={{ fontSize: '0.75rem' }}>
              <i className="fas fa-shield-alt text-success me-1"></i>
              Cumplimiento Estricto NOM-035-STPS-2018
            </small>
          </div>
        </div>

        {/* Footer */}
        <div className="card-footer text-center py-3 bg-light border-0" style={{ borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px' }}>
          <small className="text-muted">
            ¿Dudas con tu acceso?{' '}
            <a href="mailto:soporte@tamix.l3m.mx" className="text-primary text-decoration-none fw-semibold">
              Soporte Técnico
            </a>
          </small>
        </div>
      </div>
    </div>
  )
}

export default LoginForm